import { useEffect, useState } from 'react';
import { GridRowModes, GridToolbarContainer, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { Box, Paper } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import { dataGridPremiumColumns } from '../../../utils/helper';
import { DataGridIdentifiers, AddButtonUi, DatagridPremiumUi } from '../../Interface';
import productionService from '../../../service/production.service';
import { ProductionIssueDialog } from './ProductionIssueDialog';
import { ProductionIssueDirection } from '../../../config/production';
import { InvManagementMethod } from '../../../config/itemGroup';
import BatchSnDialog from '../../BatchSn/BatchSnDialog';
import BatchSnLink from '../../BatchSn/BatchSnLink';

const EditToolbar = (props) => {
  const { disabled, setDialogOpen, setIssueDirection } = props;

  const handleClick = (direction) => {
    setIssueDirection(direction);
    setDialogOpen(true);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={() => handleClick(ProductionIssueDirection.OUTGOING)} text="Kiadás" />
      <AddButtonUi
        disabled={disabled}
        onClick={() => handleClick(ProductionIssueDirection.INCOMING)}
        text="Visszavét"
      />
    </GridToolbarContainer>
  );
};

const ProductionIssues = (props) => {
  const { selectedValues, disabled } = props;
  const [reports, setReports] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [issueDirection, setIssueDirection] = useState(ProductionIssueDirection.OUTGOING);
  const [batchSnDialogRow, setBatchSnDialogRow] = useState(null);

  const columns = dataGridPremiumColumns([
    {
      field: 'docNumber',
      headerName: 'Biz. szám',
      type: 'string',
      valueGetter: (_data, row) => row.doc.docNum,
    },
    {
      field: 'itemCode',
      headerName: 'Cikkszám',
      type: 'string',
    },
    {
      field: 'itemName',
      headerName: 'Cikknév',
      type: 'string',
    },
    {
      field: 'batchSn',
      headerName: 'Sarzs',
      type: 'string',
      renderCell: (data) => <BatchSnLink row={data.row} onOpen={setBatchSnDialogRow} />,
    },
    {
      field: 'qty',
      headerName: 'Mennyiség',
      type: 'string',
      filterable: false,
      renderCell: (data) => {
        let quantity = data.row.qty;

        if (data.row.item.invManagementMethod === InvManagementMethod.BATCH) quantity = data.row.batchQty;
        return quantity;
      },
    },
    {
      field: 'quantityUnitCode',
      headerName: 'Me.',
      type: 'string',
      filterable: false,
      sortable: false,
    },
    {
      field: 'direction',
      headerName: 'Irány',
      type: 'string',
      filterable: false,
      sortable: false,
      renderCell: (data) => {
        return data.row.direction === ProductionIssueDirection.INCOMING ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: 0.5 }}>
            Visszavét
            <ArrowDropUpIcon sx={{ color: 'green', fontSize: 30 }} />
          </Box>
        ) : (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', columnGap: 0.5 }}>
            Kiadás
            <ArrowDropDownIcon sx={{ color: 'red', fontSize: 30 }} />
          </Box>
        );
      },
    },
  ]);

  useEffect(() => {
    const mergedReports = [
      ...(selectedValues?.issues ?? []).map((issue) => ({
        ...issue,
        direction: ProductionIssueDirection.OUTGOING,
        key: `ISSUE_${issue.docId}_${issue.lineNum}_${issue.batchSnId ?? 0}`,
      })),
      ...(selectedValues?.returns ?? []).map((issue) => ({
        ...issue,
        direction: ProductionIssueDirection.INCOMING,
        key: `RETURN_${issue.docId}_${issue.lineNum}_${issue.batchSnId ?? 0}`,
      })),
    ];
    setReports(mergedReports || []);
  }, [selectedValues]);

  return (
    <>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Paper elevation={2} sx={{ overflow: 'hidden', boxShadow: 3, borderRadius: 2, width: '100%' }}>
            <DatagridPremiumUi
              datagridIdentifier={DataGridIdentifiers.ProductionIssue}
              height={480}
              columns={columns}
              rows={reports}
              slots={{
                toolbar: EditToolbar,
              }}
              slotProps={{
                toolbar: {
                  disabled,
                  setDialogOpen,
                  setIssueDirection,
                },
              }}
              headerFilters={false}
              getRowHeight={() => 'auto'}
              getRowId={(row) => row.key}
            />
          </Paper>
        </Box>
      </Box>
      <ProductionIssueDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        direction={issueDirection}
        onSave={(data) => {
          setReports((prev) => [...prev, data]);
          setDialogOpen(false);
        }}
        {...props}
      />
      <BatchSnDialog
        open={Boolean(batchSnDialogRow)}
        onClose={() => setBatchSnDialogRow(null)}
        row={batchSnDialogRow}
      />
    </>
  );
};

export default ProductionIssues;
